import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";

const cards = [
  {
    title: "Missar mellan kök och service",
    text: "Beställningar, ändringar och information når inte alltid fram tydligt.",
  },
  {
    title: "Onödiga avbrott i arbetet",
    text: "Personalen måste fråga, dubbelkolla och lösa småproblem mitt i stressen.",
  },
  {
    title: "Otydlig menyhantering",
    text: "När menyer, komponenter och ändringar inte är tydliga blir arbetet långsammare.",
  },
  {
    title: "Svårt att hålla samma kvalitet i stress",
    text: "När tempot ökar blir det svårare att hålla struktur, flyt och service på samma nivå.",
  },
];

const railCards = [...cards, ...cards];

const CARD_WIDTH = 320;
const GAP = 24;
const SET_WIDTH = cards.length * (CARD_WIDTH + GAP);
const DURATION = 40;

const ProblemSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "center center"],
  });

  const headerOpacity = useTransform(scrollYProgress, [0, 0.45], [0, 1]);
  const headerY = useTransform(scrollYProgress, [0, 0.45], [48, 0]);

  return (
    <section
      ref={ref}
      className="relative bg-background py-[100px] md:py-[120px] overflow-hidden"
    >
      {/* Header */}
      <div className="max-w-[1080px] mx-auto px-6">
        <motion.div
          style={{ opacity: headerOpacity, y: headerY }}
          className="max-w-[560px] mb-14 sm:mb-16"
        >
          <span
            className="inline-block text-[12px] font-semibold tracking-widest uppercase mb-4"
            style={{ color: "#026133" }}
          >
            Problem i vardagen
          </span>
          <h2 className="text-[clamp(1.75rem,3.5vw,2.5rem)] font-bold tracking-tight leading-[1.15] text-balance text-foreground">
            Känner ni igen er i det här?
          </h2>
          <p
            className="mt-5 text-base sm:text-[17px] leading-[1.7] max-w-[480px]"
            style={{ color: "rgba(15,23,42,0.7)" }}
          >
            I många restauranger försvinner tid, tempo och kvalitet i onödiga
            missar mellan kök, service och menyhantering.
          </p>
        </motion.div>
      </div>

      {/* Infinite rail */}
      <div className="relative">
        {/* Edge fades — light */}
        <div
          className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to right, #F9FAFB, rgba(249,250,251,0))",
          }}
        />
        <div
          className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to left, #F9FAFB, rgba(249,250,251,0))",
          }}
        />

        <div
          className="overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            className="flex will-change-transform py-2"
            style={{
              gap: `${GAP}px`,
              animation: `problem-rail-drift ${DURATION}s linear infinite`,
              animationPlayState: paused ? "paused" : "running",
            }}
          >
            {railCards.map((card, i) => (
              <div
                key={`${card.title}-${i}`}
                className="problem-card soft-card flex-shrink-0 p-7 sm:p-8"
                style={{ width: `${CARD_WIDTH}px` }}
              >
                <h3 className="text-[15px] sm:text-base font-semibold leading-snug mb-2.5 text-foreground">
                  {card.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: "rgba(15,23,42,0.7)" }}
                >
                  {card.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes problem-rail-drift {
          0% { transform: translateX(0); }
          100% { transform: translateX(-${SET_WIDTH}px); }
        }
        .problem-card {
          transition: transform 0.4s cubic-bezier(0.22,1,0.36,1),
                      box-shadow 0.4s cubic-bezier(0.22,1,0.36,1);
        }
        .problem-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 40px rgba(0,0,0,0.08);
        }
      `}</style>
    </section>
  );
};

export default ProblemSection;
